import * as React from 'react';
import { toReportNumber, reportDisplay, formatReportNumber } from './reportNumbers';
import ActionButton from '../../ActionButton';
import {
  LineChart,
  BarChart,
  PieChart,
  ScatterChart,
} from '@mui/x-charts';
import {
  ResponsiveContainer,
  Sankey as RechartsSankey,
  Tooltip as RechartsTooltip,
} from 'recharts';
import {
  Box,
  IconButton,
  Card,
  CardContent,
  Typography,
} from '@mui/material';

const SERIES_COLORS = [
  '#2563eb',
  '#10b981',
  '#f59e0b',
  '#8b5cf6',
  '#ef4444',
  '#06b6d4',
  '#ec4899',
  '#84cc16',
  '#f97316',
  '#6366f1',
];

const formatSankeyDuration = (link) => {
  const seconds = toReportNumber(
    link?.avg_bump_time_seconds ??
      link?.bump_seconds ??
      link?.duration_seconds
  );
  const minutes = toReportNumber(link?.avg_bump_minutes);
  const resolvedMinutes = Number.isFinite(seconds) ? seconds / 60 : minutes;

  if (!Number.isFinite(resolvedMinutes)) return null;
  if (resolvedMinutes < 1) return `${Math.round(resolvedMinutes * 60)} sec`;
  return `${resolvedMinutes.toFixed(1)} min`;
};

export default function Chart({
  // Basic chart configuration
  type = 'line',
  title = 'Chart',
  subtitle,
  headerAction,
  summary,
  footnote,
  loading = false,
  error = '',
  onRetry,
  minChartWidth = 0,

  // Data
  data = [],

  // Axes
  xAxis = [],
  yAxis = [],

  // Axis titles
  xAxisTitle,
  yAxisTitle,

  // Series
  series = [],

  // Dimensions
  height = 320,

  // Appearance
  isDark = false,

  // Optional
  showTitle = true,
  showSubtitle = true,
  showLegend = true,

  // Interaction
  onItemClick,

  // Sankey
  sankeyOptions = {},
  sankeyLevelLegend = [],

  // Layout
  layout = 'vertical',

  // Styling
  className = '',
}) {
  // ------------------------------------------------------------
  // Theme colors
  // ------------------------------------------------------------
  const textColor = isDark ? '#e5e7eb' : '#374151';
  const secondaryTextColor = isDark ? '#9ca3af' : '#6b7280';
  const gridColor = isDark ? '#374151' : '#e5e7eb';

  const cardBackground = isDark ? '#111827' : '#ffffff';
  const legendRef = React.useRef(null);
  const [legendScroll, setLegendScroll] = React.useState({
    canLeft: false,
    canRight: false,
  });
  const useSingleLineLegend =
    showLegend && type !== 'pie' && type !== 'sankey';

  const formattedSeries = React.useMemo(
    () =>
      series.map((entry, index) => ({
        ...entry,
        // Missing line/bar values remain gaps; invalid scatter/pie points cannot be plotted.
        ...(Array.isArray(entry.data) ? { data: type === 'scatter'
          ? entry.data.filter(point => toReportNumber(point.x) != null && toReportNumber(point.y) != null)
            .map(point => ({ ...point, x: toReportNumber(point.x), y: toReportNumber(point.y) }))
          : type === 'pie'
            ? entry.data.filter(point => toReportNumber(point.value) != null)
              .map(point => ({ ...point, value: toReportNumber(point.value) }))
            : entry.data.map(toReportNumber) } : {}),
        valueFormatter: (value, context) => {
          if (value == null || (typeof value === 'number' && !Number.isFinite(value))) return '-';
          return reportDisplay(entry.valueFormatter ? entry.valueFormatter(value, context)
            : typeof value === 'object' ? (type === 'scatter' ? `${formatReportNumber(value.x)}, ${formatReportNumber(value.y)}` : formatReportNumber(value.value))
              : formatReportNumber(value));
        },
        color: entry.color || SERIES_COLORS[index % SERIES_COLORS.length],
      })),
    [series, type]
  );

  // Normalize only measure keys, leaving category names and drill-down identifiers untouched.
  const chartData = React.useMemo(() => Array.isArray(data) ? data.map(row => {
    const result = { ...row };
    series.forEach(entry => {
      if (entry.dataKey) result[entry.dataKey] = toReportNumber(row[entry.dataKey]);
    });
    return result;
  }) : data, [data, series]);

  const legendEntries = React.useMemo(
    () => formattedSeries.filter((entry) => entry.label),
    [formattedSeries]
  );

  const updateLegendScroll = React.useCallback(() => {
    const element = legendRef.current;
    if (!element) return;

    setLegendScroll({
      canLeft: element.scrollLeft > 1,
      canRight:
        element.scrollLeft + element.clientWidth < element.scrollWidth - 1,
    });
  }, []);

  React.useEffect(() => {
    updateLegendScroll();
    window.addEventListener('resize', updateLegendScroll);
    return () => window.removeEventListener('resize', updateLegendScroll);
  }, [legendEntries, updateLegendScroll]);

  const scrollLegend = (direction) => {
    const element = legendRef.current;
    if (!element) return;

    element.scrollBy({
      left: direction * Math.max(180, element.clientWidth * 0.7),
      behavior: 'smooth',
    });
  };

  // ------------------------------------------------------------
  // Format X-axis
  // ------------------------------------------------------------
  const formattedXAxis = React.useMemo(() => {
    return xAxis.map((axis) => ({
      ...axis,
      // MUI reserves measured axis space separately from the chart margin.
      height: axis.height ?? 'auto',

      // Only use the default title if the axis itself
      // doesn't already have a label.
      label: axis.label || xAxisTitle,

      tickLabelStyle: {
        fill: textColor,
        fontSize: 11,
        ...axis.tickLabelStyle,
      },

      labelStyle: {
        fill: secondaryTextColor,
        fontSize: 12,
        ...axis.labelStyle,
      },
    }));
  }, [
    xAxis,
    xAxisTitle,
    textColor,
    secondaryTextColor,
  ]);

  // ------------------------------------------------------------
  // Format Y-axis
  // ------------------------------------------------------------
  const formattedYAxis = React.useMemo(() => {
    return yAxis.map((axis) => ({
      ...axis,
      width: axis.width ?? 'auto',

      label: axis.label || yAxisTitle,

      tickLabelStyle: {
        fill: textColor,
        fontSize: 11,
        ...axis.tickLabelStyle,
      },

      labelStyle: {
        fill: secondaryTextColor,
        fontSize: 12,
        ...axis.labelStyle,
      },
    }));
  }, [
    yAxis,
    yAxisTitle,
    textColor,
    secondaryTextColor,
  ]);

  const sankeyData = React.useMemo(
    () => ({
      nodes: Array.isArray(data?.nodes) ? data.nodes : [],
      links: Array.isArray(data?.links) ? data.links : [],
    }),
    [data]
  );

  const sankeyLegendEntries = React.useMemo(() => {
    if (sankeyLevelLegend.length) return sankeyLevelLegend;

    const levels = new Map();
    sankeyData.nodes.forEach((node, index) => {
      const level = node.level ?? node.stage ?? 'flow';
      if (!levels.has(level)) {
        levels.set(level, {
          key: level,
          label: node.levelLabel || node.stageLabel || String(level),
          color:
            node.fill ||
            sankeyOptions.levelColors?.[level] ||
            SERIES_COLORS[index % SERIES_COLORS.length],
        });
      }
    });
    return Array.from(levels.values());
  }, [sankeyData.nodes, sankeyLevelLegend, sankeyOptions.levelColors]);

  const renderSankeyNode = (props) => {
    const { x, y, width, height: nodeHeight, payload, index } = props;
    const level = payload?.level ?? payload?.stage;
    const fill =
      payload?.fill ||
      sankeyOptions.levelColors?.[level] ||
      SERIES_COLORS[index % SERIES_COLORS.length];
    const isTerminal = !payload?.targetLinks?.length;
    const labelX = isTerminal ? x - 8 : x + width + 8;

    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={Math.max(nodeHeight, 2)}
          rx={4}
          ry={4}
          fill={fill}
          stroke={isDark ? '#64748b' : '#cbd5e1'}
          strokeWidth={1}
        />
        {nodeHeight >= 10 && (
          <text
            x={labelX}
            y={y + nodeHeight / 2}
            dy="0.35em"
            textAnchor={isTerminal ? 'end' : 'start'}
            fill={textColor}
            fontSize={11}
            fontWeight={500}
          >
            {payload?.display_name || payload?.name || payload?.label || `Node ${index + 1}`}
          </text>
        )}
      </g>
    );
  };

  const renderSankeyLink = (props) => {
    const {
      sourceX,
      sourceY,
      sourceControlX,
      targetX,
      targetY,
      targetControlX,
      linkWidth,
      payload,
    } = props;
    const stroke =
      payload?.color || payload?.source?.fill || sankeyOptions.linkColor || '#94a3b8';

    return (
      <path
        d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
        fill="none"
        stroke={stroke}
        strokeWidth={Math.max(linkWidth, 1)}
        strokeOpacity={sankeyOptions.linkOpacity ?? 0.32}
        style={{ transition: 'stroke-opacity 150ms ease' }}
      />
    );
  };

  const renderSankeyTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const outer = payload[0]?.payload || {};
    const item = outer.payload || outer;
    const source = item.source?.name || item.source?.label;
    const target = item.target?.name || item.target?.label;
    const value = Number(item.value ?? payload[0]?.value ?? 0);
    const duration = formatSankeyDuration(item);

    return (
      <Box
        sx={{
          p: 1.25,
          borderRadius: 1.5,
          border: `1px solid ${isDark ? '#475569' : '#d1d5db'}`,
          backgroundColor: isDark ? 'rgba(17, 24, 39, 0.96)' : 'rgba(255, 255, 255, 0.97)',
          color: textColor,
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.16)',
          fontSize: 12,
        }}
      >
        <Box sx={{ fontWeight: 600, mb: 0.5 }}>
          {source && target ? `${source} → ${target}` : item.name || item.label}
        </Box>
        {Number.isFinite(value) && <div>{item.metrics ? 'Item-screen routes' : 'Flow volume'}: {value.toLocaleString()}</div>}
        {item.ticket_count != null && <div>Distinct tickets: {item.ticket_count}</div>}
        {item.metrics?.map(metric => <div key={metric.label} style={{ marginTop: 6 }}>
          <div>{metric.label}</div>
          <div>Avg {formatReportNumber(metric.mean, 1, ' min')} · P90 {formatReportNumber(metric.p90, 1, ' min')} · {formatReportNumber(metric.count)} item-screen samples</div>
        </div>)}
        {item.metrics?.length === 0 && <div>No valid timing samples for this connection.</div>}
        {duration && <div>Avg. bump time: {duration}</div>}
        {item.screen_name && <div>Screen: {item.screen_name}</div>}
        {item.table_name && <div>Customer table: {item.table_name}</div>}
      </Box>
    );
  };

  // ------------------------------------------------------------
  // Shared chart props
  // ------------------------------------------------------------
  const commonProps = {
    height,
    hideLegend: !showLegend || useSingleLineLegend,

    margin: {
      top: showLegend && !useSingleLineLegend ? 42 : 8,
      right: 12,
      bottom: 8,
      left: 12,
    },

    sx: {
      width: '100%',
      backgroundColor: 'transparent',

      // ------------------------------------------
      // Axis tick labels
      // ------------------------------------------
      '& .MuiChartsAxis-tickLabel': {
        fill: `${textColor} !important`,
        color: textColor,
        fontSize: 11,
      },

      // ------------------------------------------
      // Axis titles
      // ------------------------------------------
      '& .MuiChartsAxis-label': {
        fill: `${secondaryTextColor} !important`,
        color: secondaryTextColor,
        fontSize: 12,
      },

      // ------------------------------------------
      // Axis lines
      // ------------------------------------------
      '& .MuiChartsAxis-line': {
        stroke: `${gridColor} !important`,
      },

      '& .MuiChartsAxis-tick': {
        stroke: `${gridColor} !important`,
      },

      // ------------------------------------------
      // Grid
      // ------------------------------------------
      '& .MuiChartsGrid-line': {
        stroke: `${gridColor} !important`,
        strokeDasharray: '3 3',
        opacity: 0.6,
      },

      // ------------------------------------------
      // Legend text
      // ------------------------------------------
      '& .MuiChartsLegend-label': {
        fill: `${textColor} !important`,
        color: textColor,
        fontSize: 12,
      },

      // Some MUI X Charts versions render legend
      // labels as SVG <text> elements.
      '& .MuiChartsLegend-series text': {
        fill: `${textColor} !important`,
      },

      // ------------------------------------------
      // Legend markers
      // ------------------------------------------
      '& .MuiChartsLegend-mark': {
        rx: 3,
      },

      // ------------------------------------------
      // Tooltip text
      // ------------------------------------------
      '& .MuiChartsTooltip-root': {
        color: textColor,
      },

      // ------------------------------------------
      // SVG text fallback
      // ------------------------------------------
      '& svg text': {
        fill: textColor,
      },

    },

    slotProps: {
      legend: {
        labelStyle: {
          fill: textColor,
          fontSize: 12,
        },
      },
    },
  };

  // ------------------------------------------------------------
  // Render chart
  // ------------------------------------------------------------
  const renderChart = () => {
    switch (type) {
      case 'bar':
        return (
          <BarChart
            layout={layout}
            dataset={chartData}
            xAxis={formattedXAxis}
            yAxis={formattedYAxis}
            series={formattedSeries}
            onItemClick={onItemClick}
            {...commonProps}
          />
        );

      case 'line':
        return (
          <LineChart
            dataset={chartData}
            xAxis={formattedXAxis}
            yAxis={formattedYAxis}
            series={formattedSeries}
            {...commonProps}
          />
        );

      case 'pie':
        return (
          <PieChart
            series={formattedSeries}
            {...commonProps}
          />
        );

      case 'scatter':
        return (
          <ScatterChart
            series={formattedSeries}
            xAxis={formattedXAxis}
            yAxis={formattedYAxis}
            {...commonProps}
          />
        );

      case 'sankey':
        return (
          <Box sx={{ position: 'relative', width: '100%', height }}>
            {showLegend && sankeyLegendEntries.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  zIndex: 2,
                  top: 0,
                  left: 8,
                  right: 8,
                  height: 30,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  whiteSpace: 'nowrap',
                  px: 1,
                  border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                  borderRadius: 1,
                  backgroundColor: isDark
                    ? 'rgba(17, 24, 39, 0.92)'
                    : 'rgba(255, 255, 255, 0.92)',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {sankeyLegendEntries.map((entry, index) => (
                  <Box
                    key={entry.key ?? entry.level ?? index}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      flex: '0 0 auto',
                      color: textColor,
                      fontSize: 11,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: '3px',
                        backgroundColor:
                          entry.color ||
                          sankeyOptions.levelColors?.[entry.key] ||
                          SERIES_COLORS[index % SERIES_COLORS.length],
                      }}
                    />
                    <span>{entry.label}</span>
                  </Box>
                ))}
              </Box>
            )}

            {sankeyData.nodes.length > 0 && sankeyData.links.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsSankey
                  data={sankeyData}
                  dataKey={sankeyOptions.dataKey || 'value'}
                  nameKey={sankeyOptions.nameKey || 'name'}
                  nodeWidth={sankeyOptions.nodeWidth ?? 14}
                  nodePadding={sankeyOptions.nodePadding ?? 24}
                  linkCurvature={sankeyOptions.linkCurvature ?? 0.5}
                  iterations={sankeyOptions.iterations ?? 48}
                  sort={sankeyOptions.sort ?? true}
                  node={renderSankeyNode}
                  link={renderSankeyLink}
                  onClick={onItemClick}
                  margin={
                    sankeyOptions.margin || {
                      top: showLegend ? 42 : 12,
                      right: 110,
                      bottom: 12,
                      left: 110,
                    }
                  }
                >
                  <RechartsTooltip content={renderSankeyTooltip} />
                </RechartsSankey>
              </ResponsiveContainer>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'grid',
                  placeItems: 'center',
                  color: secondaryTextColor,
                  fontSize: 13,
                }}
              >
                No flow data available.
              </Box>
            )}
          </Box>
        );

      default:
        return (
          <LineChart
            dataset={chartData}
            xAxis={formattedXAxis}
            yAxis={formattedYAxis}
            series={formattedSeries}
            {...commonProps}
          />
        );
    }
  };

  // ------------------------------------------------------------
  // Card
  // ------------------------------------------------------------
  return (
    <Box
      className={className}
      sx={{
        width: '100%',
      }}
    >
      <Card
        variant="outlined"
        className="reports-panel reports-chart"
        sx={{
          width: '100%',
          backgroundColor: cardBackground,
          borderColor: isDark ? '#374151' : '#e5e7eb',
          borderRadius: 2,
          boxShadow: isDark
            ? '0 1px 3px rgba(0, 0, 0, 0.25)'
            : '0 1px 3px rgba(0, 0, 0, 0.08)',
        }}
      >
        <CardContent
          sx={{
            p: 2.5,

            '&:last-child': {
              pb: 2.5,
            },
          }}
        >
          {/* Header */}
          {(showTitle || headerAction) && (
            <Box className="reports-chart-heading" sx={{ mb: 1.5, ...(headerAction ? { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' } : {}) }}>
              {showTitle && <Box sx={{ minWidth: 0, ...(headerAction ? { flex: '1 1 280px' } : {}) }}>
              <Typography
                variant="subtitle1"
                component="h3"
                className="reports-panel-title"
                sx={{
                  fontWeight: 500,
                  color: isDark ? '#bfdbfe' : '#1d4ed8',
                }}
              >
                {title}
              </Typography>

              {showSubtitle && subtitle && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    color: secondaryTextColor,
                    lineHeight: 1.43,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
              </Box>}
              {headerAction && <Box sx={{ flex: '0 1 auto', minWidth: 0, maxWidth: '100%', ml: 'auto', color: textColor }}>{headerAction}</Box>}
            </Box>
          )}

          {useSingleLineLegend && legendEntries.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                height: 30,
                mb: 0.5,
              }}
            >
              <IconButton
                size="small"
                aria-label="Show previous legend labels"
                disabled={!legendScroll.canLeft}
                onClick={() => scrollLegend(-1)}
                sx={{
                  width: 26,
                  height: 26,
                  flex: '0 0 auto',
                  color: textColor,
                }}
              >
                <span aria-hidden="true">‹</span>
              </IconButton>

              <Box
                ref={legendRef}
                onScroll={updateLegendScroll}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  minWidth: 0,
                  flex: 1,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  whiteSpace: 'nowrap',
                  scrollBehavior: 'smooth',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {legendEntries.map((entry, index) => (
                  <Box
                    key={`${entry.dataKey || entry.id || entry.label}-${index}`}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      flex: '0 0 auto',
                      color: textColor,
                      fontSize: 12,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '3px',
                        backgroundColor: entry.color,
                        flex: '0 0 auto',
                      }}
                    />
                    <span>{entry.label}</span>
                  </Box>
                ))}
              </Box>

              <IconButton
                size="small"
                aria-label="Show more legend labels"
                disabled={!legendScroll.canRight}
                onClick={() => scrollLegend(1)}
                sx={{
                  width: 26,
                  height: 26,
                  flex: '0 0 auto',
                  color: textColor,
                }}
              >
                <span aria-hidden="true">›</span>
              </IconButton>
            </Box>
          )}

          {/* Chart */}
          {summary && <Typography variant="body2" sx={{ mb: 1, color: textColor }}>{summary}</Typography>}
          {loading ? (
            <Box role="status" sx={{ minHeight: height, display: 'grid', placeItems: 'center', color: textColor }}>Loading chart…</Box>
          ) : error ? (
            <Box role="alert" sx={{ minHeight: height, p: 2, color: textColor }}>
              <Typography variant="body2">{error}</Typography>
              {onRetry && <ActionButton onClick={onRetry} sx={{ mt: 1 }}>Retry</ActionButton>}
            </Box>
          ) : minChartWidth ? (
            <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{ minWidth: minChartWidth }}>{renderChart()}</Box>
            </Box>
          ) : renderChart()}
          {footnote && <Typography variant="caption" component="p" sx={{ mt: 1, color: textColor, opacity: 0.7 }}>{footnote}</Typography>}
        </CardContent>
      </Card>
    </Box>
  );
}
